import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { CreditCard, Search } from 'lucide-react';
import { ApiError } from '@/shared/api-client/workspace';

import { cn } from '@/shared/lib/utils';
import { formatDateInVenue } from '@/shared/i18n/formatDate';
import { Badge } from '@/shared/ui/components/badge';
import { Button } from '@/shared/ui/components/button';
import { Input } from '@/shared/ui/components/input';
import { Textarea } from '@/shared/ui/components/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/ui/components/tabs';
import { Forbidden } from '@/modules/auth/Forbidden';
import { useAuth } from '@/modules/auth/AuthContext';
import { useAuthorization } from '@/modules/auth/useAuthorization';
import { useVenueContext } from '@/modules/venue/VenueContext';
import {
  useMvpHolder,
  useMvpHolderSearch,
  useVoidOrRefundMvp,
  type MvpHolderPass,
  type MvpStatus,
  type RefundMethod,
} from './holders';
import { useCustomer, useCustomerSearch, type CustomerProfile } from './customers';
import { VisitsPanel } from './VisitsPanel';
import { StandardPassVisitsPanel } from './StandardPassVisitsPanel';
import { MvpCardNumberGenerator } from './MvpCardNumberGenerator';

type TFunction = ReturnType<typeof useTranslation>['t'];

interface DateFormat {
  locale: string;
  timezone: string;
}

const REFUND_METHODS: readonly RefundMethod[] = ['cash', 'card_manual'];

const STATUS_CLASS: Record<MvpStatus, string> = {
  LIVE: 'bg-emerald-600 text-white',
  USED_UP: 'bg-slate-500 text-white',
  EXPIRED: 'bg-amber-500 text-white',
  VOIDED: 'bg-red-700 text-white',
  REFUNDED: 'bg-violet-700 text-white',
};

/** An instant as a date on the Venue's calendar. */
function instantDate(iso: string, format: DateFormat): string {
  return formatDateInVenue(iso, format);
}

/** A Venue-local `YYYY-MM-DD` day, formatted without shifting it through any timezone. */
function localDate(day: string, format: DateFormat): string {
  return formatDateInVenue(`${day}T00:00:00Z`, { locale: format.locale, timezone: 'UTC' });
}

/**
 * Operations › Customers (#517, variant B of #502): search a CustomerProfile by name, phone or a
 * scanned MVP card, then read the hours left across their live MVPs at this Venue, each MVP with
 * its derived status and the Sale it was sold on, and every MVP card ever bound to them. Staff who
 * may view Bookings also see the customer's Standard Pass visits (ADR 0072 §8). Gated by
 * `customer.view`; the backend 403 is the authoritative gate. The printer card-number generator sits
 * behind its own `mvp.sell` check and never opens the page on its own (#550).
 */
export function CustomersPage() {
  const { t, i18n } = useTranslation('common');
  const auth = useAuthorization();
  const { venues } = useAuth();
  const { venueId } = useVenueContext();
  const canView = auth.can('customer.view', { kind: 'venue', venueId });
  const canViewMvp = auth.can('mvp.view', { kind: 'venue', venueId });
  const canSell = auth.can('mvp.sell', { kind: 'venue', venueId });
  const format: DateFormat = {
    locale: i18n.language,
    timezone: venues.find((venue) => venue.id === venueId)?.timezone ?? 'UTC',
  };

  const [searchParams] = useSearchParams();
  const linkedHolderId = searchParams.get('holderId');
  const [draft, setDraft] = useState('');
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(linkedHolderId);

  useEffect(() => {
    setSelectedId(linkedHolderId);
  }, [linkedHolderId, venueId]);

  const search = useCustomerSearch(venueId, query, canView);
  const mvpSearch = useMvpHolderSearch(venueId, query, canView && canViewMvp);
  const holder = useCustomer(venueId, selectedId, canView);
  const matches = search.data ?? [];

  // A scanned card or an exact match has one holder: open them straight away.
  useEffect(() => {
    const only = search.data?.length === 1 ? search.data[0] : undefined;
    if (only) setSelectedId(only.id);
  }, [search.data]);

  if (!canView) return <Forbidden />;

  function submit(event: FormEvent) {
    event.preventDefault();
    setQuery(draft.trim());
  }

  return (
    <section className="grid gap-4 p-6">
      <header className="grid gap-1">
        <h1 className="text-2xl font-semibold">{t('mvpCustomers.title')}</h1>
        <p className="text-muted-foreground">{t('mvpCustomers.description')}</p>
      </header>

      {canSell && <MvpCardNumberGenerator />}

      <div className="grid gap-6 md:grid-cols-[280px_1fr]">
        <aside className="grid content-start gap-2">
          <form className="flex gap-2" role="search" onSubmit={submit}>
            <div className="relative flex-1">
              <Search className="absolute top-2.5 left-2.5 size-4 text-muted-foreground" />
              <Input
                className="pl-8"
                aria-label={t('mvpCustomers.searchLabel')}
                placeholder={t('mvpCustomers.searchPlaceholder')}
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
              />
            </div>
            <Button type="submit" variant="outline" disabled={!draft.trim()}>
              {t('mvpCustomers.search')}
            </Button>
          </form>
          {search.isFetching ? (
            <p className="text-sm text-muted-foreground">{t('mvpCustomers.searching')}</p>
          ) : search.isError ? (
            <p role="alert" className="text-sm text-destructive">
              {t('mvpCustomers.searchFailed')}
            </p>
          ) : query && matches.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {t('mvpCustomers.noMatches', { query })}
            </p>
          ) : null}
          <ul className="grid gap-1">
            {matches.map((match) => (
              <li key={match.id}>
                <button
                  type="button"
                  onClick={() => setSelectedId(match.id)}
                  aria-current={match.id === selectedId}
                  className={cn(
                    'flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-sm hover:bg-accent',
                    match.id === selectedId && 'bg-accent font-medium',
                  )}
                >
                  <span>
                    {match.name}
                    {match.phone ? (
                      <span className="block text-xs text-muted-foreground">{match.phone}</span>
                    ) : null}
                  </span>
                  {canViewMvp &&
                  (mvpSearch.data?.find((item) => item.id === match.id)?.liveHours ?? 0) > 0 ? (
                    <span className="rounded-full bg-emerald-100 px-2 text-xs text-emerald-800">
                      {t('mvpCustomers.liveHoursPill', {
                        hours: mvpSearch.data?.find((item) => item.id === match.id)?.liveHours,
                      })}
                    </span>
                  ) : null}
                </button>
              </li>
            ))}
          </ul>
        </aside>

        {selectedId === null ? (
          <p className="text-sm text-muted-foreground">{t('mvpCustomers.pickHint')}</p>
        ) : holder.isPending ? (
          <p className="text-sm text-muted-foreground">{t('mvpCustomers.loading')}</p>
        ) : holder.isError ? (
          <p role="alert" className="text-sm text-destructive">
            {t('mvpCustomers.loadFailed')}
          </p>
        ) : (
          <Profile t={t} holder={holder.data} format={format} venueId={venueId} />
        )}
      </div>
    </section>
  );
}

function Profile({
  t,
  holder,
  format,
  venueId,
}: {
  t: TFunction;
  holder: CustomerProfile;
  format: DateFormat;
  venueId: string;
}) {
  const auth = useAuthorization();
  const canViewMvp = auth.can('mvp.view', { kind: 'venue', venueId });
  const mvp = useMvpHolder(venueId, holder.id, canViewMvp);
  const passes = mvp.data?.multiVisitPasses ?? [];
  const cards = mvp.data?.cards ?? [];
  const canVoid = auth.can('mvp.void', { kind: 'venue', venueId });
  const canRefund = auth.can('mvp.refund', { kind: 'venue', venueId });
  const canViewStandardVisits = auth.can('booking.view', { kind: 'venue', venueId });
  return (
    <div className="grid content-start gap-4">
      <div className="flex items-start justify-between gap-4 rounded-lg border p-4">
        <div>
          <h2 className="text-xl font-semibold">
            {holder.firstName} {holder.lastName}
          </h2>
          <p className="text-sm text-muted-foreground">
            {[holder.phone, holder.email].filter(Boolean).join(' · ')}
          </p>
        </div>
        {canViewMvp && mvp.data ? (
          <div className="text-right">
            <div className="text-3xl font-semibold tabular-nums" data-testid="live-hours">
              {t('mvpCustomers.headline', { hours: mvp.data.liveHours })}
            </div>
            <div className="text-xs text-muted-foreground">
              {t('mvpCustomers.headlineCaption', { count: mvp.data.liveCount })}
            </div>
          </div>
        ) : null}
      </div>

      {canViewMvp && mvp.isPending ? (
        <p className="text-sm text-muted-foreground">{t('mvpCustomers.loading')}</p>
      ) : canViewMvp && mvp.isError ? (
        <p role="alert" className="text-sm text-destructive">
          {t('mvpCustomers.loadFailed')}
        </p>
      ) : null}

      {canViewMvp || canViewStandardVisits ? (
        <Tabs defaultValue={canViewMvp ? 'passes' : 'standardVisits'}>
          <TabsList>
            {canViewMvp ? (
              <>
                <TabsTrigger value="passes">
                  {t('mvpCustomers.tabs.passes', { count: passes.length })}
                </TabsTrigger>
                <TabsTrigger value="cards">
                  {t('mvpCustomers.tabs.cards', { count: cards.length })}
                </TabsTrigger>
                <TabsTrigger value="visits">{t('mvpCustomers.tabs.visits')}</TabsTrigger>
              </>
            ) : null}
            {canViewStandardVisits ? (
              <TabsTrigger value="standardVisits">
                {t('mvpCustomers.tabs.standardVisits')}
              </TabsTrigger>
            ) : null}
          </TabsList>

          {canViewMvp ? (
            <>
              <TabsContent value="passes" className="grid gap-3">
                {passes.map((pass) => (
                  <PassCard
                    key={pass.id}
                    t={t}
                    pass={pass}
                    format={format}
                    venueId={venueId}
                    customerId={holder.id}
                    canVoid={canVoid}
                    canRefund={canRefund}
                  />
                ))}
                {mvp.data && passes.length === 0 ? (
                  <p className="text-sm text-muted-foreground">{t('mvpCustomers.noPasses')}</p>
                ) : null}
              </TabsContent>

              <TabsContent value="cards" className="grid gap-2">
                {cards.map((card) => (
                  <div
                    key={card.id}
                    className="flex items-center gap-3 rounded-md border p-3 text-sm"
                  >
                    <CreditCard className="size-4" />
                    <span className="font-mono">{card.maskedCard}</span>
                    <span className="text-muted-foreground">
                      {t('mvpCustomers.cardBound', { date: instantDate(card.boundAt, format) })}
                    </span>
                    <span
                      className={cn(
                        'ml-auto text-xs',
                        card.status === 'ACTIVE' ? 'text-emerald-700' : 'text-red-600',
                      )}
                    >
                      {t(`mvpCustomers.cardStatus.${card.status}`)}
                    </span>
                  </div>
                ))}
                {mvp.data && cards.length === 0 ? (
                  <p className="text-sm text-muted-foreground">{t('mvpCustomers.noCards')}</p>
                ) : null}
              </TabsContent>

              <TabsContent value="visits">
                <VisitsPanel venueId={venueId} customerId={holder.id} format={format} />
              </TabsContent>
            </>
          ) : null}

          {canViewStandardVisits ? (
            <TabsContent value="standardVisits">
              <StandardPassVisitsPanel venueId={venueId} customerId={holder.id} format={format} />
            </TabsContent>
          ) : null}
        </Tabs>
      ) : (
        <p className="text-sm text-muted-foreground">{t('mvpCustomers.noAllowedHistory')}</p>
      )}
    </div>
  );
}

function expiryLabel(t: TFunction, pass: MvpHolderPass, format: DateFormat): string {
  const date = localDate(pass.expiresOn, format);
  if (pass.status === 'EXPIRED') return t('mvpCustomers.expired', { date });
  if (pass.status === 'USED_UP') return t('mvpCustomers.expires', { date });
  if (pass.daysLeft === 0) return t('mvpCustomers.expiresToday', { date });
  return t('mvpCustomers.expiresIn', { date, count: pass.daysLeft });
}

function weekdaysLabel(t: TFunction, pass: MvpHolderPass): string {
  if (pass.validWeekdays.length === 0) return t('mvpCustomers.everyDay');
  return pass.validWeekdays.map((day) => t(`products.form.availability.weekday.${day}`)).join(', ');
}

function PassCard({
  t,
  pass,
  format,
  venueId,
  customerId,
  canVoid,
  canRefund,
}: {
  t: TFunction;
  pass: MvpHolderPass;
  format: DateFormat;
  venueId: string;
  customerId: string;
  canVoid: boolean;
  canRefund: boolean;
}) {
  const voidRefund = useVoidOrRefundMvp(venueId, customerId);
  const [action, setAction] = useState<'void' | 'refund' | null>(null);
  const [reason, setReason] = useState('');
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState<RefundMethod | null>(null);
  const live = pass.status === 'LIVE';
  const soldOn = instantDate(pass.soldAt, format);
  const receipt = pass.receiptNumber ?? pass.saleId;
  return (
    <article
      aria-label={pass.optionName}
      className={cn('grid gap-2 rounded-lg border p-4', !live && 'bg-muted/40')}
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-semibold">{pass.optionName}</span>
        <Badge className={STATUS_CLASS[pass.status]}>
          {t(`mvpCustomers.status.${pass.status}`)}
        </Badge>
        {live && !pass.validToday ? (
          <span className="text-xs text-amber-600">{t('mvpCustomers.notValidToday')}</span>
        ) : null}
        {pass.status === 'EXPIRED' ? (
          <span className="text-xs text-amber-700">
            {t('mvpCustomers.lostHours', { count: pass.balance })}
          </span>
        ) : null}
      </div>
      <div className="grid gap-1">
        <span className={cn('text-sm tabular-nums', !live && 'text-muted-foreground')}>
          {t('mvpCustomers.balance', { left: pass.balance, hours: pass.hours })}
        </span>
        <div className="h-1.5 overflow-hidden rounded-full bg-muted">
          <div
            className={cn('h-full', live ? 'bg-emerald-500' : 'bg-muted-foreground/40')}
            style={{ width: `${(pass.balance / pass.hours) * 100}%` }}
          />
        </div>
      </div>
      <p className="text-xs text-muted-foreground">
        {expiryLabel(t, pass, format)} · {weekdaysLabel(t, pass)} · {pass.price} {pass.currency} ·{' '}
        {pass.soldBy
          ? t('mvpCustomers.soldBy', { date: soldOn, receipt, seller: pass.soldBy })
          : t('mvpCustomers.sold', { date: soldOn, receipt })}
      </p>
      {pass.voidedAt && pass.voidReason ? (
        <p className="text-sm text-muted-foreground">
          {t('mvpCustomers.voidRefund.voidRecord', {
            date: instantDate(pass.voidedAt, format),
            reason: pass.voidReason,
            by: pass.voidedBy ?? '',
          })}
        </p>
      ) : null}
      {pass.refundedAt && pass.refundReason ? (
        <p className="text-sm text-muted-foreground">
          {t(
            pass.refundMethod
              ? 'mvpCustomers.voidRefund.refundRecordWithMethod'
              : 'mvpCustomers.voidRefund.refundRecord',
            {
              date: instantDate(pass.refundedAt, format),
              reason: pass.refundReason,
              by: pass.refundedBy ?? '',
              amount: pass.refundAmount?.amount ?? '',
              currency: pass.refundAmount?.currency ?? pass.currency,
              method: pass.refundMethod
                ? t(`mvpCustomers.voidRefund.methodRecord.${pass.refundMethod}`)
                : '',
            },
          )}
        </p>
      ) : null}
      {action ? (
        <form
          className="grid gap-2 rounded-md border p-3"
          onSubmit={(event) => {
            event.preventDefault();
            if (!reason.trim()) return;
            voidRefund.mutate(
              {
                passId: pass.id,
                kind: action,
                reason: reason.trim(),
                amount: action === 'refund' ? amount.trim() : undefined,
                method: action === 'refund' && method ? method : undefined,
              },
              {
                onSuccess: () => {
                  setAction(null);
                  setReason('');
                  setAmount('');
                  setMethod(null);
                },
              },
            );
          }}
        >
          <p className="font-medium">
            {t(`mvpCustomers.voidRefund.${action}Title`, { name: pass.optionName })}
          </p>
          <label className="text-sm" htmlFor={`reason-${pass.id}`}>
            {t('mvpCustomers.voidRefund.reason')}
          </label>
          <Textarea
            id={`reason-${pass.id}`}
            required
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
          {action === 'refund' ? (
            <>
              <label className="text-sm" htmlFor={`amount-${pass.id}`}>
                {t('mvpCustomers.voidRefund.amount', {
                  price: pass.price,
                  currency: pass.currency,
                })}
              </label>
              <Input
                id={`amount-${pass.id}`}
                type="number"
                step="0.01"
                min="0.01"
                max={pass.price}
                required
                value={amount}
                onChange={(event) => setAmount(event.target.value)}
              />
              <fieldset className="grid gap-1">
                <legend className="text-sm">{t('mvpCustomers.voidRefund.method')}</legend>
                <div className="flex gap-4">
                  {REFUND_METHODS.map((option) => (
                    <label key={option} className="flex items-center gap-2 text-sm">
                      <input
                        type="radio"
                        name={`method-${pass.id}`}
                        value={option}
                        required
                        checked={method === option}
                        onChange={() => setMethod(option)}
                      />
                      {t(`mvpCustomers.voidRefund.methods.${option}`)}
                    </label>
                  ))}
                </div>
              </fieldset>
            </>
          ) : null}
          {voidRefund.isError ? (
            <p role="alert" className="text-sm text-destructive">
              {voidRefund.error instanceof ApiError
                ? voidRefund.error.message
                : t('mvpCustomers.voidRefund.failed')}
            </p>
          ) : null}
          <div className="flex gap-2">
            <Button
              type="submit"
              disabled={
                voidRefund.isPending ||
                !reason.trim() ||
                (action === 'refund' && (!amount || !method))
              }
            >
              {t('mvpCustomers.voidRefund.confirm')}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setAction(null);
                voidRefund.reset();
              }}
            >
              {t('mvpCustomers.voidRefund.cancel')}
            </Button>
          </div>
        </form>
      ) : (
        <div className="flex gap-2">
          {canVoid && !pass.voidedAt ? (
            <Button variant="outline" onClick={() => setAction('void')}>
              {t('mvpCustomers.voidRefund.void')}
            </Button>
          ) : null}
          {canRefund && !pass.refundedAt ? (
            <Button variant="outline" onClick={() => setAction('refund')}>
              {t('mvpCustomers.voidRefund.refund')}
            </Button>
          ) : null}
        </div>
      )}
    </article>
  );
}
